<template>
  <transition name="xt-modal-fade">
    <div v-if="visible" class="xt-modal-mask" @click.self="handleCancel">
      <transition name="xt-modal-pop" appear>
        <div v-if="visible" class="xt-modal-dialog" :class="dialogClass">
          <!-- 图标区 -->
          <div v-if="icon" class="xt-modal-icon-wrap" :class="iconType">
            <span class="xt-modal-icon">{{ icon }}</span>
          </div>

          <!-- 标题 -->
          <h3 v-if="title" class="xt-modal-title">{{ title }}</h3>

          <!-- 正文（纯文本） -->
          <p v-if="message && mode !== 'input'" class="xt-modal-message">{{ message }}</p>

          <!-- 输入框模式 -->
          <div v-if="mode === 'input'" class="xt-modal-input-wrap">
            <input
              v-if="inputType === 'text'"
              ref="inputRef"
              :value="localValue"
              type="text"
              class="xt-modal-input"
              :placeholder="inputPlaceholder"
              @input="onInput"
              @keydown.enter="handleConfirm"
              @keydown.esc="handleCancel"
            />
            <input
              v-else-if="inputType === 'password'"
              ref="inputRef"
              :value="localValue"
              type="password"
              class="xt-modal-input"
              :placeholder="inputPlaceholder"
              @input="onInput"
              @keydown.enter="handleConfirm"
              @keydown.esc="handleCancel"
            />
            <input
              v-else-if="inputType === 'email'"
              ref="inputRef"
              :value="localValue"
              type="email"
              class="xt-modal-input"
              :placeholder="inputPlaceholder"
              @input="onInput"
              @keydown.enter="handleConfirm"
              @keydown.esc="handleCancel"
            />
          </div>

          <!-- 自定义内容插槽 -->
          <slot v-else name="default"></slot>

          <!-- 按钮区 -->
          <div class="xt-modal-actions" :class="{ 'single-actions': !showCancel }">
            <button
              v-if="showCancel"
              class="xt-modal-btn xt-modal-btn-cancel"
              :disabled="loading"
              @click="handleCancel"
            >
              {{ cancelText }}
            </button>
            <button
              class="xt-modal-btn xt-modal-btn-confirm"
              :class="confirmClass"
              :disabled="loading"
              @click="handleConfirm"
            >
              <span v-if="loading" class="xt-modal-spinner"></span>
              {{ loading ? loadingText : confirmText }}
            </button>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'XtModal',
  props: {
    visible: { type: Boolean, default: false },
    mode: { type: String, default: 'alert' },       // alert | confirm | input
    title: { type: String, default: '' },
    message: { type: String, default: '' },
    icon: { type: String, default: '' },
    iconType: { type: String, default: 'info' },    // info | success | warning | danger
    confirmText: { type: String, default: '确定' },
    cancelText: { type: String, default: '取消' },
    loadingText: { type: String, default: '处理中...' },
    showCancel: { type: Boolean, default: true },
    confirmType: { type: String, default: 'primary' }, // primary | danger
    inputType: { type: String, default: 'text' },
    inputPlaceholder: { type: String, default: '' },
    inputValue: { type: [String, Number], default: '' },
    loading: { type: Boolean, default: false },
    closeOnMask: { type: Boolean, default: true },
    closeOnEsc: { type: Boolean, default: true }
  },
  data() {
    return {
      localValue: ''
    }
  },
  computed: {
    dialogClass() {
      return [
        'xt-modal-' + this.mode,
        'xt-modal-icon-' + this.iconType
      ]
    },
    confirmClass() {
      return 'xt-modal-btn-' + this.confirmType
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.localValue = this.inputValue || ''
      }
      if (val && this.mode === 'input') {
        this.$nextTick(function () {
          var input = this.$refs.inputRef
          if (input) input.focus()
        })
      }
      if (val && this.closeOnEsc) {
        document.addEventListener('keydown', this.handleKey)
      }
      if (!val) {
        document.removeEventListener('keydown', this.handleKey)
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKey)
  },
  methods: {
    onInput(e) {
      this.localValue = e.target.value
      this.$emit('update:inputValue', this.localValue)
    },
    handleKey(e) {
      if (e.key === 'Escape') {
        this.handleCancel()
      }
    },
    handleCancel() {
      if (!this.closeOnMask) return
      this.$emit('update:visible', false)
      this.$emit('cancel')
    },
    handleConfirm() {
      this.$emit('confirm', this.localValue)
    }
  }
}
</script>

<style scoped>
/* ===== 遮罩层 ===== */
.xt-modal-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 20px;
}

/* ===== 弹窗主体 ===== */
.xt-modal-dialog {
  background: #ffffff;
  border-radius: 16px;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 32px 28px 24px;
  min-width: 320px;
  max-width: 420px;
  width: 100%;
  text-align: center;
  position: relative;
  overflow: hidden;
}

/* 顶部装饰线 */
.xt-modal-dialog::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #1a9c8e, #5cc6b7);
  border-radius: 16px 16px 0 0;
}

.xt-modal-dialog.xt-modal-icon-success::before {
  background: linear-gradient(90deg, #52c41a, #95de64);
}
.xt-modal-dialog.xt-modal-icon-warning::before {
  background: linear-gradient(90deg, #faad14, #ffd666);
}
.xt-modal-dialog.xt-modal-icon-danger::before {
  background: linear-gradient(90deg, #ff4d4f, #ff7875);
}
.xt-modal-dialog.xt-modal-icon-info::before {
  background: linear-gradient(90deg, #1890ff, #69c0ff);
}

/* ===== 图标 ===== */
.xt-modal-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  background: #e6f7f5;
  transition: transform 0.3s ease;
}

.xt-modal-icon-wrap.success {
  background: #f6ffed;
}
.xt-modal-icon-wrap.warning {
  background: #fffbe6;
}
.xt-modal-icon-wrap.danger {
  background: #fff1f0;
}
.xt-modal-icon-wrap.info {
  background: #e6f4ff;
}

.xt-modal-icon {
  font-size: 32px;
  line-height: 1;
}

/* ===== 标题 ===== */
.xt-modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 12px;
  line-height: 1.5;
}

/* ===== 正文 ===== */
.xt-modal-message {
  font-size: 14px;
  color: #5a5a5a;
  margin: 0 0 24px;
  line-height: 1.7;
  word-break: break-word;
}

/* ===== 输入框 ===== */
.xt-modal-input-wrap {
  margin: 0 0 24px;
  text-align: left;
}

.xt-modal-input {
  width: 100%;
  padding: 12px 16px;
  font-size: 15px;
  color: #1a1a1a;
  background: #f7f8fa;
  border: 1.5px solid #e4e7ed;
  border-radius: 10px;
  outline: none;
  transition: all 0.25s ease;
  box-sizing: border-box;
  font-family: inherit;
}

.xt-modal-input:hover {
  border-color: #c4c9d2;
  background: #fff;
}

.xt-modal-input:focus {
  border-color: #1a9c8e;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(26, 156, 142, 0.12);
}

.xt-modal-input::placeholder {
  color: #b0b3b8;
}

/* ===== 按钮区 ===== */
.xt-modal-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 8px;
}

.xt-modal-actions.single-actions {
  justify-content: flex-end;
}

.xt-modal-btn {
  flex: 1;
  max-width: 140px;
  padding: 11px 24px;
  font-size: 15px;
  font-weight: 500;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.xt-modal-btn:active:not(:disabled) {
  transform: scale(0.97);
}

.xt-modal-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.xt-modal-btn-cancel {
  background: #f2f3f5;
  color: #4e5969;
  border: 1.5px solid #e4e7ed;
  flex: 0 0 100px;
  max-width: 100px;
}

.xt-modal-btn-cancel:hover:not(:disabled) {
  background: #e4e7ed;
  color: #1a1a1a;
}

.xt-modal-btn-confirm {
  background: #1a9c8e;
  color: #fff;
  box-shadow: 0 2px 8px rgba(26, 156, 142, 0.25);
}

.xt-modal-btn-confirm:hover:not(:disabled) {
  background: #158f82;
  box-shadow: 0 4px 14px rgba(26, 156, 142, 0.35);
}

.xt-modal-btn-confirm-danger {
  background: #ff4d4f;
  box-shadow: 0 2px 8px rgba(255, 77, 79, 0.25);
}

.xt-modal-btn-confirm-danger:hover:not(:disabled) {
  background: #e63946;
  box-shadow: 0 4px 14px rgba(255, 77, 79, 0.35);
}

/* ===== Loading Spinner ===== */
.xt-modal-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: xt-spin 0.7s linear infinite;
}

@keyframes xt-spin {
  to { transform: rotate(360deg); }
}

/* ===== 动画 ===== */
.xt-modal-fade-enter-active,
.xt-modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.xt-modal-fade-enter,
.xt-modal-fade-leave-to {
  opacity: 0;
}

.xt-modal-pop-enter-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.xt-modal-pop-leave-active {
  transition: all 0.2s ease;
}

.xt-modal-pop-enter {
  opacity: 0;
  transform: scale(0.85) translateY(10px);
}

.xt-modal-pop-leave-to {
  opacity: 0;
  transform: scale(0.92) translateY(5px);
}

/* ===== 移动端适配 ===== */
@media (max-width: 480px) {
  .xt-modal-mask {
    padding: 16px;
    align-items: flex-end;
  }

  .xt-modal-dialog {
    min-width: unset;
    max-width: 100%;
    width: 100%;
    padding: 28px 20px 20px;
    border-radius: 18px 18px 12px 12px;
    animation: xt-mobile-slide-up 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .xt-modal-icon-wrap {
    width: 56px;
    height: 56px;
    margin-bottom: 16px;
  }

  .xt-modal-icon {
    font-size: 28px;
  }

  .xt-modal-title {
    font-size: 17px;
  }

  .xt-modal-message {
    font-size: 13px;
    margin-bottom: 20px;
  }

  .xt-modal-actions {
    gap: 10px;
  }

  .xt-modal-btn {
    flex: 1;
    max-width: none;
    padding: 12px 20px;
    font-size: 15px;
  }

  .xt-modal-btn-cancel {
    flex: 0 0 110px;
    max-width: 110px;
  }
}

@keyframes xt-mobile-slide-up {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ===== 超小屏 ===== */
@media (max-width: 360px) {
  .xt-modal-dialog {
    padding: 24px 16px 16px;
  }

  .xt-modal-title {
    font-size: 16px;
  }

  .xt-modal-btn {
    font-size: 14px;
    padding: 10px 16px;
  }
}
</style>
